import React, { useEffect, useRef, useState } from "react";
import { Modal } from "../ds/composite/Modal";
import { Field, InlineNotice, Pill } from "../ds";
import toast from "../ds/composite/Toast";
import {
  useCreateTicket,
  useUploadTicketAttachment,
} from "../../hooks/useSupportTickets";
import { getErrorMessage } from "../../utils/errorHandling";
import { environmentService } from "../../config/environment.config";
import {
  TICKET_ATTACHMENT_ALLOWED_EXTENSIONS,
  TICKET_ATTACHMENT_MAX_FILES,
  TICKET_ATTACHMENT_MAX_SIZE_MB,
  TICKET_BODY_MAX_LENGTH,
  TICKET_SUBJECT_MAX_LENGTH,
  TICKET_TYPE_LABELS,
  TICKET_PRIORITY_LABELS,
} from "../../constants/supportTicket";
import {
  TicketPriority,
  TicketType,
  type SupportTicketDetail,
} from "../../types/supportTicket";

const MAX_SIZE_BYTES = TICKET_ATTACHMENT_MAX_SIZE_MB * 1024 * 1024;

const AttachIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21.44 11.05 12.25 20.24a5.5 5.5 0 0 1-7.78-7.78l9.19-9.19a3.5 3.5 0 0 1 4.95 4.95L9.41 17.41a1.5 1.5 0 0 1-2.12-2.12l8.49-8.49" />
  </svg>
);

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getExtension = (fileName: string): string =>
  fileName.split(".").pop()?.toLowerCase() ?? "";

const validateFile = (file: File): string | null => {
  const ext = getExtension(file.name);
  if (!TICKET_ATTACHMENT_ALLOWED_EXTENSIONS.includes(ext)) {
    return `${file.name}: file type not allowed`;
  }
  if (file.size > MAX_SIZE_BYTES) {
    return `${file.name}: must be ≤ ${TICKET_ATTACHMENT_MAX_SIZE_MB} MB`;
  }
  return null;
};

interface FormState {
  type: TicketType;
  priority: TicketPriority;
  subject: string;
  body: string;
}

interface FormErrors {
  subject?: string;
  body?: string;
  files?: string;
}

const INITIAL_FORM: FormState = {
  type: TicketType.BUG,
  priority: TicketPriority.NORMAL,
  subject: "",
  body: "",
};

interface SubmitTicketDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (ticket: SupportTicketDetail) => void;
  /** Prefills the Subject field, e.g. derived from the current page. */
  initialSubject?: string;
  /** Auto-captured screenshot of the screen the user reported from. */
  initialScreenshot?: File | null;
}

export const SubmitTicketDialog: React.FC<SubmitTicketDialogProps> = ({
  open,
  onClose,
  onSuccess,
  initialSubject,
  initialScreenshot,
}) => {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [files, setFiles] = useState<File[]>([]);
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [includeScreenshot, setIncludeScreenshot] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [pageUrl, setPageUrl] = useState("");
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: createTicket, isPending: submitting } =
    useCreateTicket();
  const { mutateAsync: uploadAttachment } = useUploadTicketAttachment();
  const busy = submitting || uploadingAttachments;

  useEffect(() => {
    if (open) {
      setForm({ ...INITIAL_FORM, subject: initialSubject ?? "" });
      setFiles([]);
      setScreenshot(initialScreenshot ?? null);
      setIncludeScreenshot(!!initialScreenshot);
      setErrors({});
      setApiError(null);
      // Snapshot at open time so the URL shown to the user matches what's
      // actually submitted, even if they navigate while the dialog is open.
      setPageUrl(window.location.href);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const screenshotUrl = React.useMemo(
    () => (screenshot ? URL.createObjectURL(screenshot) : null),
    [screenshot],
  );
  useEffect(() => {
    return () => {
      if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    };
  }, [screenshotUrl]);

  const screenshotSlot = includeScreenshot && screenshot ? 1 : 0;

  const handleAddFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const incoming = Array.from(fileList);
    const combined = [...files, ...incoming];

    if (combined.length + screenshotSlot > TICKET_ATTACHMENT_MAX_FILES) {
      setErrors((prev) => ({
        ...prev,
        files: `Max ${TICKET_ATTACHMENT_MAX_FILES} files`,
      }));
      return;
    }

    for (const file of incoming) {
      const error = validateFile(file);
      if (error) {
        setErrors((prev) => ({ ...prev, files: error }));
        return;
      }
    }

    setErrors((prev) => ({ ...prev, files: undefined }));
    setFiles(combined);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!form.subject.trim()) {
      newErrors.subject = "Subject is required";
    } else if (form.subject.length > TICKET_SUBJECT_MAX_LENGTH) {
      newErrors.subject = `Subject must be ≤ ${TICKET_SUBJECT_MAX_LENGTH} characters`;
    }

    if (!form.body.trim()) {
      newErrors.body = "Description is required";
    } else if (form.body.length > TICKET_BODY_MAX_LENGTH) {
      newErrors.body = `Description must be ≤ ${TICKET_BODY_MAX_LENGTH} characters`;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setApiError(null);
      const ticket = await createTicket({
        type: form.type,
        priority: form.priority,
        subject: form.subject.trim(),
        body: form.body.trim(),
        pageUrl,
        browser: navigator.userAgent,
        os: navigator.platform,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        appVersion: environmentService.appVersion,
      });

      const messageId = ticket.messages[0]?.id;
      const attachmentFiles =
        screenshotSlot > 0 && screenshot ? [screenshot, ...files] : files;

      if (attachmentFiles.length > 0) {
        setUploadingAttachments(true);
        const results = await Promise.allSettled(
          attachmentFiles.map((file) =>
            uploadAttachment({ ticketId: ticket.id, file, messageId }),
          ),
        );
        setUploadingAttachments(false);
        const failedCount = results.filter(
          (r) => r.status === "rejected",
        ).length;
        if (failedCount > 0) {
          toast.error(
            `Ticket ${ticket.ticketNumber} created, but ${failedCount} attachment${
              failedCount > 1 ? "s" : ""
            } failed to upload`,
          );
        }
      }

      toast.success(`Ticket ${ticket.ticketNumber} submitted`);
      onSuccess?.(ticket);
      onClose();
    } catch (err: unknown) {
      setApiError(getErrorMessage(err, "Failed to submit ticket"));
      setUploadingAttachments(false);
    }
  };

  const handleClose = () => {
    if (!busy) onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="New support ticket"
      size="md"
      footer={
        <>
          <button
            type="button"
            className="btn sm ghost"
            onClick={handleClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn sm accent"
            onClick={() => void handleSubmit()}
            disabled={busy}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            {busy && (
              <span
                className="spinner"
                aria-label="Submitting"
                style={{ width: 14, height: 14 }}
              />
            )}
            {submitting
              ? "Submitting…"
              : uploadingAttachments
                ? "Uploading attachments…"
                : "Submit ticket"}
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {apiError && <InlineNotice tone="risk">{apiError}</InlineNotice>}

        <InlineNotice tone="info">
          This report will include where you are.
          <br />
          Page:{" "}
          <a
            href={pageUrl}
            target="_blank"
            rel="noreferrer"
            style={{ color: "inherit", wordBreak: "break-all" }}
          >
            {pageUrl}
          </a>
        </InlineNotice>

        <InlineNotice tone="warn">
          Do not include PHI in this ticket. Redact screenshots and attachments
          before uploading — Support staff will see the description,
          attachments, IP, and location on this ticket.
        </InlineNotice>

        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <Field label="Type" required>
              <select
                className="input"
                value={form.type}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    type: e.target.value as TicketType,
                  }))
                }
                disabled={busy}
              >
                {Object.values(TicketType).map((value) => (
                  <option key={value} value={value}>
                    {TICKET_TYPE_LABELS[value]}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Priority" required>
              <select
                className="input"
                value={form.priority}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    priority: e.target.value as TicketPriority,
                  }))
                }
                disabled={busy}
              >
                {Object.values(TicketPriority).map((value) => (
                  <option key={value} value={value}>
                    {TICKET_PRIORITY_LABELS[value]}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </div>

        <Field
          label="Subject"
          required
          error={errors.subject}
          helpRight={
            <span
              className="mono"
              style={{ fontSize: 11, color: "var(--ink-3)" }}
            >
              {form.subject.length} / {TICKET_SUBJECT_MAX_LENGTH}
            </span>
          }
        >
          <input
            className={`input${errors.subject ? " error" : ""}`}
            value={form.subject}
            maxLength={TICKET_SUBJECT_MAX_LENGTH}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, subject: e.target.value }))
            }
            disabled={busy}
            autoFocus
          />
        </Field>

        <Field
          label="Description"
          required
          error={errors.body}
          hint={
            !errors.body ? "Steps to reproduce, expected vs actual…" : undefined
          }
          helpRight={
            <span
              className="mono"
              style={{ fontSize: 11, color: "var(--ink-3)" }}
            >
              {form.body.length} / {TICKET_BODY_MAX_LENGTH}
            </span>
          }
        >
          <textarea
            className={`input${errors.body ? " error" : ""}`}
            rows={5}
            value={form.body}
            maxLength={TICKET_BODY_MAX_LENGTH}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, body: e.target.value }))
            }
            disabled={busy}
          />
        </Field>

        {screenshot && (
          <Field label="Screenshot" optional>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  position: "relative",
                  width: 96,
                  height: 64,
                  borderRadius: "var(--r-sm)",
                  overflow: "hidden",
                  border: "1px solid var(--line)",
                  opacity: includeScreenshot ? 1 : 0.4,
                  flexShrink: 0,
                }}
              >
                {screenshotUrl && (
                  <img
                    src={screenshotUrl}
                    alt="Screenshot of the current page"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                )}
                {includeScreenshot && (
                  <button
                    type="button"
                    onClick={() => setIncludeScreenshot(false)}
                    disabled={busy}
                    aria-label="Remove screenshot"
                    style={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      border: "none",
                      background: "var(--bg)",
                      color: "var(--ink-2)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      lineHeight: 1,
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
              <label className={`check${busy ? " disabled" : ""}`}>
                <input
                  type="checkbox"
                  checked={includeScreenshot}
                  onChange={(e) => setIncludeScreenshot(e.target.checked)}
                  disabled={busy}
                />
                <span className="box">
                  <svg
                    viewBox="0 0 10 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 4L3.5 6.5L9 1"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                Include screenshot of this page
              </label>
            </div>
          </Field>
        )}

        <Field
          label="Attachments"
          optional
          error={errors.files}
          hint={`Screenshots, logs, or docs. Max ${TICKET_ATTACHMENT_MAX_FILES} files, ${TICKET_ATTACHMENT_MAX_SIZE_MB} MB each. ${TICKET_ATTACHMENT_ALLOWED_EXTENSIONS.join(", ")}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={TICKET_ATTACHMENT_ALLOWED_EXTENSIONS.map(
              (ext) => `.${ext}`,
            ).join(",")}
            style={{ display: "none" }}
            onChange={(e) => {
              handleAddFiles(e.target.files);
              e.target.value = "";
            }}
            disabled={
              busy ||
              files.length + screenshotSlot >= TICKET_ATTACHMENT_MAX_FILES
            }
          />
          <button
            type="button"
            className="btn sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={
              busy ||
              files.length + screenshotSlot >= TICKET_ATTACHMENT_MAX_FILES
            }
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <AttachIcon />
            Attach files
          </button>

          {files.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginTop: 8,
              }}
            >
              {files.map((file, index) => (
                <Pill key={`${file.name}-${index}`} tone="ghost">
                  {file.name} ({formatFileSize(file.size)}){" "}
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    disabled={busy}
                    aria-label={`Remove ${file.name}`}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "inherit",
                      marginLeft: 4,
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                </Pill>
              ))}
            </div>
          )}
        </Field>
      </div>
    </Modal>
  );
};

export default SubmitTicketDialog;
